<template>
  <VLayout row :wrap="['xs', 'sm'].includes($mq)" class="equipment-assignment">
    <VFlex
      :class="[
        flexStyles,
        'daily-list-item--title',
        'equipment-col--grow',
        'flex-grow-print flex-3_12-print',
      ]"
    >
      <ListItemDisplayOnly :value="asset.EQUIPMENT_NAME" />
    </VFlex>
    <VFlex
      :class="[
        flexStyles,
        'equipment-col--grow',
        'flex-grow-print flex-3_12-print',
      ]"
    >
      <BasePicklist
        :disabled="disabled"
        :title="asset.LABORER_NAME"
        :value="asset.LABORER_NAME"
        :items="operators"
        return-object
        clearable
        hint="Operator / Driver"
        item-text="[0].identifier"
        @click:clear="$emit('update', { LABORER: null })"
        v-on="$listeners"
        @input="$emit('update', { LABORER: $event[0].id })"
      />
    </VFlex>
    <VFlex
      :class="[
        flexStyles,
        'equipment-col--grow',
        'flex-grow-print flex-2_12-print',
      ]"
    >
      <ListItemText
        :disabled="disabled"
        :value="asset.EQUIPMENT_NOTES"
        hint="Equip. Notes"
        @input="
          $emit('update', {
            EQUIPMENT_NOTES: $event,
          })
        "
        @focus="$emit('editing')"
        @keydown="$emit('editing')"
      />
    </VFlex>
    <VFlex
      :class="[flexStyles, 'equipment-col--total-hours', 'flex-1_12-print']"
    >
      <ListItemAssignmentHoursDisplay
        :value="totalHours"
        hint="Total Hours"
        :delta="totalHoursDelta"
      />
    </VFlex>
    <VFlex :class="[flexStyles, 'equipment-col--reg', 'flex-1_12-print']">
      <ListItemDisplayOnly :value="regularHoursMinusStandby" hint="Reg" />
    </VFlex>
    <VFlex
      :class="[
        flexStyles,
        'equipment-col--standby-hours',
        'flex-1_12-print',
        'standby-hours-cell',
      ]"
    >
      <ListItemNumber
        :disabled="disabled"
        :value="standbyHours"
        :min="0"
        :max="regularHours"
        :step="0.5"
        :rules="[standbyHoursValidation]"
        hint="Standby Hours"
        @input="
          $emit('update', { NUMBER_STANDBY_HOURS: parseFloat($event) || 0 })
        "
        @focus="$emit('editing')"
        @keydown="$emit('editing')"
      />
      <VIcon v-if="isOnStandby" size="1em" class="print-only ml-1"
        >star_outline</VIcon
      >
    </VFlex>
    <VFlex
      :class="[
        flexStyles,
        'equipment-col--fully-standby',
        'flex-1_12-print',
        'no-print',
      ]"
    >
      <VSwitch
        :input-value="isOnStandby"
        :disabled="disabled"
        aria-label="Fully Standby"
        color="blue"
        class="ma-0 pa-0 no-print"
        hide-details
        @change="setStandby"
      />
    </VFlex>
    <VFlex
      v-if="!isShiftItemsModeActive"
      key="non-billable"
      :class="[flexStyles, 'equipment-col--non-billable', 'flex-1_12-print']"
    >
      <ListItemCheckbox
        :disabled="disabled"
        :value="asset.NON_BILLABLE || false"
        label="Non Billable"
        hide-details
        @change="$emit('update', { NON_BILLABLE: $event })"
      />
    </VFlex>
    <ShiftItemActions
      v-else-if="!isMultiSplitOn"
      key="split-btn"
      :class="[flexStyles, 'equipment-col--shift-items', 'flex-1_12-print']"
      :shift-items="shiftItems"
      :is-list-visible="isShiftItemsListVisible"
      :is-working="asset.isWorking"
      @add="addShiftItem"
      @toggle="toggleShiftItemsListVisibility(shiftId)"
    />
    <VFlex
      v-else
      key="multi-split-checkbox"
      :class="[flexStyles, 'equipment-col--shift-items', 'flex-1_12-print']"
    >
      <ListItemCheckbox
        :value="isMultiSplitSelected"
        small
        color="blue"
        class="no-print"
        hide-details
        @change="
          toggleMultiSplitSelection({ assetType, shiftId, isSelected: $event })
        "
      />
    </VFlex>
  </VLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { VSwitch } from 'vuetify/lib'
import {
  LaborerLocations,
  AssetTypes,
  EQUIPMENT_SHIFT_HOURS,
} from '@constants/knack'
import FlexStylesMixin from '@mixins/flex-styles'
import _get from 'lodash/get'
import getTimeInMinutes from '@utils/getTimeInMinutes'

import ShiftItemActions from '@components/DailyReportView/ShiftItemActions'

export default {
  name: 'AssignmentEquipment',
  components: {
    ShiftItemActions,
    VSwitch,
  },
  mixins: [FlexStylesMixin(8)],
  props: {
    asset: {
      type: Object,
      required: true,
      default: () => {},
    },
    operators: {
      type: Array,
      required: true,
      default: () => [],
    },
    draft: {
      type: Object,
      required: true,
      default: () => {},
    },
    isShiftItemsModeActive: {
      type: Boolean,
      required: true,
      default: false,
    },
    isMultiSplitOn: {
      type: Boolean,
      required: true,
      default: false,
    },
  }, // props
  data() {
    return {
      assetType: AssetTypes.EQUIPMENT,
      LaborerLocations,
      totalHours: EQUIPMENT_SHIFT_HOURS,
      regularHours: EQUIPMENT_SHIFT_HOURS,
      overtimeHours: 0,
    }
  },
  computed: {
    ...mapGetters([
      'getIsShiftItemsListVisible',
      'getShiftItemsForShift',
      'getLocation',
      'getIsMultiSplitSelected',
    ]),
    shiftId() {
      return this.asset.ID
    },
    disabled() {
      return this.draft.isWorking || false
    }, // disabled
    shiftItems() {
      return this.getShiftItemsForShift(this.assetType, this.shiftId) || []
    },
    isShiftItemsListVisible() {
      return this.getIsShiftItemsListVisible(this.shiftId)
    },

    startTimeMinutes() {
      let time = _get(this.draft, 'START_TIME', this.asset.START_TIME)
      return getTimeInMinutes(time)
    }, // startTimeMinutes

    endTimeMinutes() {
      let time = _get(this.draft, 'END_TIME', this.asset.END_TIME)
      return getTimeInMinutes(time)
    }, // endTimeMinutes

    totalShiftItemHours() {
      return this.totalShiftItemHoursReg + this.totalShiftItemHoursOT
    },
    totalShiftItemHoursReg() {
      return +this.shiftItems.reduce((acc, shiftItem) => {
        return acc + shiftItem.REGULAR_HOURS
      }, 0)
    },
    totalShiftItemHoursOT() {
      return +this.shiftItems.reduce((acc, shiftItem) => {
        return acc + shiftItem.OT_HOURS
      }, 0)
    },
    regularHoursDelta() {
      if (!this.shiftItems.length) {
        return 0
      }
      return this.totalShiftItemHoursReg - this.regularHours
    },
    overtimeHoursDelta() {
      if (!this.shiftItems.length) {
        return 0
      }
      return this.totalShiftItemHoursOT - this.overtimeHours
    },
    totalHoursDelta() {
      if (!this.shiftItems.length) {
        return 0
      }
      return this.totalShiftItemHours - this.totalHours
    },
    hasShiftItemHoursError() {
      if (!this.shiftItems.length) {
        return false
      }
      return (
        this.regularHoursDelta +
          this.overtimeHoursDelta +
          this.totalHoursDelta !==
        0
      )
    },
    isMultiSplitSelected() {
      return this.getIsMultiSplitSelected(this.assetType, this.asset.ID)
    },
    standbyHours() {
      return (
        _get(
          this.draft,
          'NUMBER_STANDBY_HOURS',
          this.asset.NUMBER_STANDBY_HOURS
        ) || 0
      )
    },
    isOnStandby() {
      return Number(this.standbyHours) >= this.regularHours
    },
    regularHoursMinusStandby() {
      const regHours = this.regularHours - this.standbyHours
      const result = Math.max(0, regHours)
      // Only show decimal if it's not a whole number
      return result % 1 === 0 ? result.toString() : result.toFixed(1)
    },
    standbyHoursValidation() {
      return (value) => {
        // Allow empty values (will be converted to 0)
        if (value === '' || value === null || value === undefined) return true

        const hours = parseFloat(value)
        if (isNaN(hours)) return 'Must be a valid number'
        if (hours < 0) return 'Cannot be negative'
        if (hours > this.regularHours) {
          return `Cannot exceed ${this.regularHours} hours`
        }
        return true
      }
    },
  },
  watch: {
    hasShiftItemHoursError: {
      handler: function(hasShiftItemHoursError) {
        this.$emit('mismatchedHours', hasShiftItemHoursError)
      },
      immediate: true,
    },
  },
  methods: {
    setStandby(isOn) {
      this.$emit('update', {
        NUMBER_STANDBY_HOURS: isOn ? this.regularHours : 0,
      })
    },
    ...mapActions([
      'addDraftShiftItem',
      'toggleShiftItemsListVisibility',
      'toggleMultiSplitSelection',
    ]),

    addShiftItem() {
      if (!this.getIsShiftItemsListVisible(this.shiftId)) {
        this.toggleShiftItemsListVisibility(this.shiftId)
      }
      let currentShiftItems = this.getShiftItemsForShift(
        this.assetType,
        this.shiftId
      )

      let regularHours = !currentShiftItems.length ? 8 : 0
      let location = this.getLocation
      let defaultPhase = _get(location, 'DEFAULT_PHASE[0]')
      let defaultCostCode = _get(location, 'DEFAULT_COST_CODE[0]')
      let newShiftItem = {
        PHASE: [defaultPhase],
        COST_CODE: [defaultCostCode],
        REGULAR_HOURS: regularHours,
        OT_HOURS: 0,
        IS_DEFAULT: !currentShiftItems.length,
      }
      this.addDraftShiftItem({
        assetType: this.assetType,
        shiftId: this.shiftId,
        shiftItem: newShiftItem,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@design';
.daily-list-item.on-standby {
  border: 2px solid #2196f3 !important;

  @media print {
    border-color: black !important;
    border-width: 2px 0 !important;
  }
}

@media print {
  .equipment-assignment .standby-hours-cell {
    display: flex;
    align-items: center;
    .pf {
      flex: 0 0 auto;
    }
  }
}

// Screen widths above the stacked mobile layout: every column grows with the
// row, text columns three times as fast as the numeric and toggle columns,
// and each has a minimum width its content needs. Non-Billable and Shift Items
// share the last slot (swapped by shift items mode, like T&M on Manpower) with
// the same growth, so toggling the mode doesn't reflow the row. CardEquipment's
// header labels use the same classes so they stay aligned. Below the minimums
// the last column wraps to a second line, header included. Print keeps its
// 12-column spans.
@media screen and (min-width: 625px) {
  .equipment-assignment,
  #equipment .list--header .layout {
    flex-wrap: wrap;
  }
  .equipment-col--shift-items {
    margin-left: auto;
  }
  #equipment .col-header {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @each $column, $grow, $min-width in (
      (grow, 3, 88px),
      (total-hours, 1, 56px),
      (reg, 1, 48px),
      (standby-hours, 1, 72px),
      (fully-standby, 1, 64px),
      (non-billable, 1.5, 72px),
      (shift-items, 1.5, 156px)
    )
  {
    .equipment-col--#{$column} {
      flex: $grow 1 0 !important;
      min-width: $min-width;
    }
  }
}
</style>
